/**
 * POSTrainingMode - Training mode toggle for POS
 * Allows switching between normal and training mode for staff training
 */
import { useState } from 'react';
import { GraduationCap, AlertTriangle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function POSTrainingMode({ isTrainingMode, onToggle }) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleToggle = () => {
    if (isTrainingMode) {
      // Exiting training mode - just toggle
      onToggle(false);
      toast.success('Training mode disabled');
    } else {
      // Entering training mode - show confirmation
      setShowConfirm(true);
    }
  };

  const confirmTrainingMode = () => {
    onToggle(true);
    setShowConfirm(false);
    toast.success('Training mode enabled - invoices will NOT be fiscal');
  };

  return (
    <>
      {/* Training Mode Badge/Button */}
      <button
        onClick={handleToggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          isTrainingMode
            ? 'bg-amber-500 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
        data-testid="training-mode-toggle"
      >
        <GraduationCap className="w-4 h-4" />
        <span className="text-sm font-medium">
          {isTrainingMode ? 'Training Mode ON' : 'Training'}
        </span>
      </button>

      {/* Training Mode Banner (shown when active) */}
      {isTrainingMode && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 px-4 z-50 flex items-center justify-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-bold">TRAINING MODE</span>
          <span className="text-amber-100">- Invoices are NOT fiscal</span>
          <button
            onClick={handleToggle}
            className="ml-4 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-sm"
          >
            Exit Training
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <GraduationCap className="w-6 h-6" />
              Enable Training Mode?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Training mode is for staff practice only.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Important:</p>
                <ul className="text-sm text-amber-700 mt-2 space-y-1">
                  <li>• Invoices will be marked as "TRAINING"</li>
                  <li>• These are NOT valid fiscal invoices</li>
                  <li>• Will show "THIS IS NOT A FISCAL INVOICE"</li>
                  <li>• Do NOT use for real customer transactions</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={confirmTrainingMode}
              className="flex-1 bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-training-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Enable Training
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Training Mode Context for POS
export const useTrainingMode = () => {
  const [isTrainingMode, setIsTrainingMode] = useState(false);

  const getInvoiceType = (baseType = 'NS') => {
    if (!isTrainingMode) return baseType;
    
    // Convert to training equivalent
    const trainingTypes = {
      'NS': 'TS',  // Normal Sale -> Training Sale
      'NR': 'TR',  // Normal Refund -> Training Refund
    };
    
    return trainingTypes[baseType] || baseType;
  };

  return {
    isTrainingMode,
    setIsTrainingMode,
    getInvoiceType,
  };
};
